import { motion } from "framer-motion";
import { ShieldCheck, Award, Users, Calendar } from "lucide-react";
import { fadeUp } from "./shared";

const trustItems = [
    {
        icon: Award,
        stat: "20+",
        label: "év tapasztalat",
        text: "Több évtizednyi szakmai tapasztalattal dolgozunk lakossági és üzleti ügyfeleinkért.",
    },
    {
        icon: ShieldCheck,
        stat: "100%",
        label: "kulcsrakész kivitelezés",
        text: "Tervezéstől az átadásig egyetlen partner — koordinált szakipari folyamatokkal.",
    },
    {
        icon: Users,
        stat: "500+",
        label: "elégedett ügyfél",
        text: "Lakossági és vállalati projektek — a családi házaktól egészen az irodaházakig.",
    },
    {
        icon: Calendar,
        stat: "0 Ft",
        label: "konzultációs díj",
        text: "Átlátható, írásos árajánlat — 30 napos érvényességgel, rejtett költségek nélkül.",
    },
];

export const TrustBadges = () => (
    <section
        id="bizalom"
        data-testid="trust-section"
        className="relative bg-white py-24 sm:py-32 lg:py-40 border-b border-black/10 overflow-hidden"
    >
        {/* Subtle grid pattern background */}
        <div
            aria-hidden
            className="absolute inset-0 opacity-[0.06] pointer-events-none"
            style={{
                backgroundImage:
                    "linear-gradient(#0D7A73 1px, transparent 1px), linear-gradient(90deg, #0D7A73 1px, transparent 1px)",
                backgroundSize: "80px 80px",
            }}
        />
        {/* Soft teal glow */}
        <div
            aria-hidden
            className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full pointer-events-none"
            style={{
                background:
                    "radial-gradient(closest-side, rgba(13,122,115,0.18), transparent 70%)",
            }}
        />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            {/* Editorial header */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 pb-16 sm:pb-24 border-b border-black/10">
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-100px" }}
                    className="lg:col-span-5 flex flex-col gap-4"
                >
                    <span className="font-mono-label text-xs text-[#0D7A73] tabular">
                        (04) &nbsp; Referencia
                    </span>
                    <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
                        Bizalom és{" "}
                        <span className="text-[#0D7A73]">tapasztalat.</span>
                    </h2>
                </motion.div>

                <motion.div
                    variants={fadeUp}
                    custom={1}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-100px" }}
                    className="lg:col-span-6 lg:col-start-7 flex items-end"
                >
                    <p className="text-slate-700 text-lg sm:text-xl leading-relaxed max-w-2xl">
                        A számok mögött két évtized szakmai munkája, több száz
                        befejezett projekt és megbízhatóság áll — a részletekre
                        figyelő kivitelezés minden területén.
                    </p>
                </motion.div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10 border-t-0">
                {trustItems.map((it, i) => {
                    const Icon = it.icon;
                    return (
                        <motion.div
                            key={it.label}
                            variants={fadeUp}
                            custom={i}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: "-80px" }}
                            data-testid={`trust-badge-${it.label}`}
                            className="group relative bg-white p-8 sm:p-10 flex flex-col gap-6 min-h-[280px]"
                        >
                            <div className="flex items-start justify-between">
                                <span className="font-mono-label text-[10px] text-slate-400 tabular">
                                    0{i + 1}
                                </span>
                                <Icon
                                    className="w-6 h-6 text-[#0D7A73] opacity-80 group-hover:opacity-100 transition-opacity"
                                    strokeWidth={1.6}
                                />
                            </div>

                            <div>
                                <div className="font-display font-extrabold text-5xl sm:text-6xl text-[#0D7A73] tabular leading-none">
                                    {it.stat}
                                </div>
                                <div className="mt-3 font-display font-bold text-lg text-[#0A0A0A] uppercase tracking-wide">
                                    {it.label}
                                </div>
                            </div>

                            <p className="mt-auto text-slate-600 text-sm leading-relaxed">
                                {it.text}
                            </p>

                            <span className="absolute left-0 bottom-0 h-[2px] w-0 bg-[#0D7A73] transition-[width] duration-700 group-hover:w-full" />
                        </motion.div>
                    );
                })}
            </div>
        </div>
    </section>
);
